import React, { FC, useState } from 'react';
import Modal from 'react-modal';
import { isLicExpired, isLicExpiringSoon, isLicIncompatibleVersion } from '../../constants/license-constants';
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import { getArrayBufferFromBlob } from '../../exporters/LicenseHelper';
import closeIcon from '../../images/close.svg';
import { useGlobalState } from '../../Providers';
import { importSoftwareLicense } from '../../services/systemService';
import { getErrorMessage } from '../../services/utilities/ServiceError';
import { useDialog } from '../dialog/DialogContext';
import Tooltip from "../Tooltip/Tooltip";
import LicenseBanner from './LicenseBanner';
import { toast } from 'react-toastify';
import ActivityFrame from '../activity-frame/activity-frame';

interface LicenseModalProps {
    isOpen: boolean;
    onClose: () => void;
}

// Constants for selection keys
const LICENSE_OPTIONS = {
    LOWEST_LIMITS: 1,
    HIGHEST_LIMITS: 2,
    FEATURES: 3
};
  
const LicenseModal: FC<LicenseModalProps> = ({ isOpen, onClose }) => {
    const { translate } = useLocale();
    const { buildInfo, loadBuildInfo } = useGlobalState() ?? {};
    const [selectedOption, setSelectedOption] = useState(LICENSE_OPTIONS.LOWEST_LIMITS);
    const [busy, setBusy] = useState<boolean>(false);
    const dialog = useDialog();

    const LIMIT_LABELS = {
        [LICENSE_OPTIONS.LOWEST_LIMITS]: translate.byKey(TranslationKeys.lowestLimits),
        [LICENSE_OPTIONS.HIGHEST_LIMITS]: translate.byKey(TranslationKeys.highestLimits),
        [LICENSE_OPTIONS.FEATURES]: translate.byKey(TranslationKeys.features)
    };
    
    const LIMIT_TOOLTIPS = {
        [LICENSE_OPTIONS.LOWEST_LIMITS]: translate.byKey(TranslationKeys.lowestLimitsTip),
        [LICENSE_OPTIONS.HIGHEST_LIMITS]: translate.byKey(TranslationKeys.highestLimitsTip),
        [LICENSE_OPTIONS.FEATURES]: translate.byKey(TranslationKeys.featuresTip)
      };

    const handleLimitsChange = (event) => {
        setSelectedOption(Number(event.target.value));
    };

    const onchange = async (event) => {
        const files = event.target.files;

        if (files.length === 0) {
            return;
        }

        setBusy(true);
        const arrayBuffer = await getArrayBufferFromBlob(files[0]);
        const licenseBytes = Object.values(arrayBuffer);
        importSoftwareLicense(licenseBytes, translate).then((response) => {
            loadBuildInfo && loadBuildInfo();
            toast.success(translate.byKey(TranslationKeys.licenseImportedSuccessfully));
        }).catch((error) => {
            dialog.pushError({ message: getErrorMessage(error), title: translate.byKey(TranslationKeys.error) })
        }).finally(() => {
            setBusy(false);
        })
    }

    const getDataToShow = () => {
        if (selectedOption === LICENSE_OPTIONS.LOWEST_LIMITS) {
            return buildInfo ?.lowestLimitsLicensed;
        }
        else if (selectedOption === LICENSE_OPTIONS.HIGHEST_LIMITS) {
            return buildInfo ?.highestLimitsLicensed;
        }   
        else if (selectedOption === LICENSE_OPTIONS.FEATURES) {
            return buildInfo ?.featuresLicensed;
        }
        else {
            return [];
        }
    }

    return <Modal
        isOpen={isOpen}
        ariaHideApp={false}
        overlayClassName="flex w-screen position-float fixed top-0 left-0 h-screen bg-opacity-75 bg-white"
        className="bg-white border-none m-auto shadow select-none outline-none overflow-hidden" >
       
        <div className={`w-full p-2 border-b flex flex-row items-center bg-sidemenu-focus text-white justify-between`}>
            <div className="flex flex-row">
                <p className="pl-3 font-bold font text-white">{translate.byKey(TranslationKeys.softwareLicense)}</p>
            </div>
            <img src={closeIcon} data-testid="modal_closeLicense" onClick={onClose} className="cursor-pointer mr-2" />
        </div>

        <div className="p-2 flex flex-col" style={{ width: '480px', height: '516px' }}>
            <ActivityFrame busy={busy} busyMessage={translate.byKey(TranslationKeys.importingLicensePleaseWait)} className='flex flex-col flex-grow bg-white'>
                <span className='font-bold text-portal-blue-primary'>{translate.byKey(TranslationKeys.license)}:</span>
                <span>{buildInfo?.licenseFullName}</span>

                {/* License expired */}
                {buildInfo ?.licenseStatus !== undefined && isLicExpired(buildInfo ?.licenseStatus) ? (
                    <LicenseBanner
                        buildInfo={buildInfo}
                        message={translate.byKeyFormatted(TranslationKeys.licenseExpiredAt, [buildInfo ?.licenseName, buildInfo ?.licenseDate])}
                        iconColor="red" />

                // License will expire soon
                ) : buildInfo ?.licenseStatus !== undefined && isLicExpiringSoon(buildInfo ?.licenseStatus) ? (
                    <LicenseBanner
                        buildInfo={buildInfo}
                        message={translate.byKeyFormatted(TranslationKeys.licenseExpiringSoon, [buildInfo ?.licenseDate])}
                        iconColor="orange" />
                ) 
                // License is incompatible with software version
                : buildInfo ?.licenseStatus !== undefined && isLicIncompatibleVersion(buildInfo ?.licenseStatus) ? (
                    <LicenseBanner
                        buildInfo={buildInfo}
                        message={translate.byKeyFormatted(TranslationKeys.licenseIncompatibleVersion, [buildInfo ?.licenseName])}
                        iconColor="orange" />
                ) : (null)}

                <span className='font-bold pt-3 text-portal-blue-primary'>{translate.byKey(TranslationKeys.licensedFor)}:</span>

                <div style={{ marginBottom: '10px' }}>
                    {Object.entries(LIMIT_LABELS).map(([key, label]) => (

                        <Tooltip
                            key={key}
                            text={LIMIT_TOOLTIPS[key]}
                            direction="bottom"
                            show={true}
                            width="max-w-xs"
                            contentPositionX="start">
                            <label key={key} style={{ display: 'inline-block', marginRight: '16px', cursor: 'pointer' }}>
                                <input
                                    type="radio"
                                    name="systemType"
                                    value={key}
                                    checked={Number(key) === selectedOption}
                                    onChange={handleLimitsChange}
                                    style={{ cursor: 'pointer' }}
                                />
                                <span style={{ marginLeft: '8px' }}>{label}</span>
                            </label>
                        </Tooltip>
                    ))}
                </div>

                <div className='overflow-y-scroll' style={{ height: '290px' }}>
                    <table width={'94%;'}> {/* Leave room for the tooltips */}
                        {(selectedOption === LICENSE_OPTIONS.LOWEST_LIMITS
                            ? buildInfo?.lowestLimitsLicensed
                            : selectedOption === LICENSE_OPTIONS.HIGHEST_LIMITS
                                ? buildInfo?.highestLimitsLicensed
                                : buildInfo?.featuresLicensed
                                )?.map((attribute, i) => {
                            const item = attribute.split(":");
                            const tooltip = item.length > 2 ? item[2] : null;

                            return <tbody key={`licenseListBody${i}`}>
                                <tr key={`licenseListRow${i}`} className='border-b'
                                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
                                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}>
                                    <td>{item[0]}</td>
                                    {tooltip ? 
                                    <td>
                                        <Tooltip text={item[2]} direction="bottom" show={true} width={'w-full'} contentPositionX='start'>
                                            {item[1]}
                                            <span className="inline-block align-left ml-2">
                                                <svg width="16" height="16" fill="currentColor" className="text-blue-500" viewBox="0 0 16 16">
                                                    <circle cx="8" cy="8" r="8" fill="currentColor" opacity="0.2" />
                                                    <text x="8" y="12" textAnchor="middle" fontSize="12" fill="currentColor">?</text>
                                                </svg>
                                            </span>
                                        </Tooltip>
                                    </td>
                                    : <td className={`text-justify${tooltip ? ' text-red-500' : ''}`}>{item[1]}</td>}
                                </tr>
                            </tbody>
                        })}
                    </table>
                </div>
                <div className='flex flex-row w-full justify-center p-2'>
                    <label htmlFor="importSoftwareLicenseBtn" className="text-white p-2 bg-sidemenu-focus rounded-lg cursor-pointer">
                        {translate.byKey(TranslationKeys.importSoftwareLicense)}
                    </label>
                    <input
                        type='file'
                        accept='.lic'
                        data-testid="btn_importSoftwareLicense"
                        id='importSoftwareLicenseBtn'
                        className='hidden'
                        onChange={(e) => onchange(e)} />
                </div>
            </ActivityFrame>
        </div>
    </Modal>
}

export default LicenseModal;
